import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Paperclip,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import { ForfaitQuantityFields } from "../components/ForfaitQuantityFields";
import {
  CurrencyAmountInput,
  formatInvoiceAmount,
  InvoiceCurrencyFields,
  isValidExchangeRateCdf,
} from "../components/InvoiceCurrencyFields";
import { useAdminInvoiceSubmit } from "../hooks/useAdminInvoiceSubmit";
import { cn } from "@/shared/lib/cn";

type Step = 0 | 1 | 2;

function formatDisplayDate(value: string) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function computeAmountToPay(totalAmount: string, rsspAmount: string) {
  return Math.max(0, (Number(totalAmount) || 0) - (Number(rsspAmount) || 0));
}

function getStep0Issues(
  form: {
    hospitalId: string;
    companyId: string;
    periodFrom: string;
    periodTo: string;
    conventionId: string;
    file: File | null;
  },
  isForfait: boolean,
  hasExistingFile: boolean,
): string[] {
  const issues: string[] = [];
  if (!form.hospitalId) issues.push("Sélectionnez un hôpital / prestataire.");
  if (!form.companyId) issues.push("Sélectionnez une entreprise.");
  if (!form.periodFrom) issues.push("Indiquez la date de début de période.");
  if (!form.periodTo) {
    issues.push("Indiquez une date de fin valide (ex. le 31 avril n'existe pas — utilisez le 30/04).");
  } else if (form.periodFrom && form.periodTo < form.periodFrom) {
    issues.push("La date de fin doit être postérieure ou égale à la date de début.");
  }
  if (!form.conventionId) issues.push("Sélectionnez une convention.");
  if (!form.file && !hasExistingFile) {
    issues.push("Joignez le fichier PDF ou image de la facture.");
  }
  return issues;
}

type AdminSubmitInvoiceWizardProps = {
  invoiceId?: number;
};

export function AdminSubmitInvoiceWizard({ invoiceId }: AdminSubmitInvoiceWizardProps = {}) {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.submit;
  const [step, setStep] = useState<Step>(0);
  const [fileLabel, setFileLabel] = useState(labels.fields.noFileSelected);
  const {
    form,
    updateForm,
    hospitals,
    hospitalName,
    companies,
    conventions,
    selectedCompany,
    selectedConvention,
    isForfait,
    isEditMode,
    existingFileName,
    loadingMeta,
    loadingEdit,
    loadingCompanies,
    loadingConventions,
    submitting,
    error,
    submittedInvoiceId,
    submit,
  } = useAdminInvoiceSubmit(invoiceId);

  const steps = [
    { key: "provider" as const, icon: Building2 },
    { key: "amounts" as const, icon: DollarSign },
    { key: "summary" as const, icon: Check },
  ];

  const hasExistingFile = Boolean(existingFileName);
  const canProceedStep0 =
    !!form.hospitalId &&
    !!form.companyId &&
    !!form.periodFrom &&
    !!form.periodTo &&
    (!form.periodFrom || form.periodTo >= form.periodFrom) &&
    !!form.conventionId &&
    (!!form.file || hasExistingFile);
  const step0Issues = getStep0Issues(form, isForfait, hasExistingFile);

  const currencyOk =
    form.invoiceCurrency !== "CDF" || isValidExchangeRateCdf(form.exchangeRateCdf);
  const canProceedStep1 =
    currencyOk &&
    (isForfait
      ? form.forfaitNbFamilles.trim().length > 0 && form.forfaitNbFamillesClient.trim().length > 0
      : form.totalAmount.trim().length > 0);
  const amountToPay = computeAmountToPay(form.totalAmount, form.rsspAmount);
  const currencyFieldLabels = {
    currency: labels.fields.currency,
    currencyHint: labels.fields.currencyHint,
    exchangeRate: labels.fields.exchangeRate,
    exchangeRateHint: labels.fields.exchangeRateHint,
    currencyUsd: labels.fields.currencyUsd,
    currencyCdf: labels.fields.currencyCdf,
  };
  const conventionLabel = selectedConvention
    ? `Convention #${selectedConvention.id}`
    : labels.summary.noConvention;

  if (submittedInvoiceId) {
    return (
      <>
        <Breadcrumbs
          homeHref="/dashboard/admin"
          items={[
            { label: t.safecheck.invoices.billing },
            { label: isEditMode ? "Modifier la facture" : labels.title },
          ]}
        />
        <div className="theme-card p-6">
          <div className="rounded-xl border border-green/30 bg-green/10 px-4 py-3 text-sm text-foreground">
            {isEditMode
              ? `Facture #${submittedInvoiceId} mise à jour avec succès.`
              : `${labels.success} (#${submittedInvoiceId})`}
          </div>
          <div className="mt-4">
            <Link
              to={`/dashboard/invoices/${submittedInvoiceId}`}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              Voir la facture
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/admin"
        items={[
          { label: t.safecheck.invoices.billing },
          { label: isEditMode ? "Modifier la facture" : labels.title },
        ]}
      />

      <div className="theme-card">
        <div className="border-b border-border px-6 py-5">
          <h1 className="text-lg font-semibold text-foreground">
            {isEditMode ? `Modifier la facture #${invoiceId}` : labels.cardTitle}
          </h1>
          <p className="mt-1 text-xs text-muted">
            {isEditMode
              ? "Modification admin des données de la facture"
              : "Soumission admin pour le compte d'un hôpital"}
            {loadingMeta || loadingEdit ? " — chargement…" : hospitalName ? ` — ${hospitalName}` : ""}
          </p>
          {error && <p className="mt-2 text-sm text-pink">{error}</p>}
        </div>

        <div className="border-b border-border px-6 py-6">
          <div className="mx-auto flex max-w-3xl items-center justify-between">
            {steps.map((item, index) => {
              const Icon = item.icon;
              const isActive = step === index;
              const isCompleted = step > index;
              return (
                <div key={item.key} className="flex flex-1 items-center">
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-full border-2",
                        isCompleted && "border-green bg-green text-white",
                        isActive && "border-primary bg-primary text-white",
                        !isActive && !isCompleted && "border-border bg-card text-muted",
                      )}
                    >
                      <Icon size={18} />
                    </div>
                    <span className="text-xs font-medium text-muted">{labels.steps[item.key]}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={cn("mx-3 mb-6 h-px flex-1", step > index ? "bg-green" : "bg-border")} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-6 p-6">
          {step === 0 && (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Hôpital / Prestataire" required>
                  <select
                    value={form.hospitalId}
                    onChange={(e) => updateForm("hospitalId", e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Sélectionner un hôpital</option>
                    {hospitals.map((h) => (
                      <option key={h.id} value={String(h.id)}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label={labels.fields.company} required>
                  <div className="relative">
                    <Briefcase size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                    <select
                      value={form.companyId}
                      onChange={(e) => {
                        updateForm("companyId", e.target.value);
                        updateForm("conventionId", "");
                      }}
                      className={`${selectClass} pl-9`}
                      disabled={!form.hospitalId || loadingCompanies}
                    >
                      <option value="">{labels.fields.companyPlaceholder}</option>
                      {companies.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </FormField>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField label={labels.fields.periodFrom} required>
                  <input type="date" value={form.periodFrom} onChange={(e) => updateForm("periodFrom", e.target.value)} className={inputClass} />
                </FormField>
                <FormField label={labels.fields.periodTo} required>
                  <input type="date" value={form.periodTo} onChange={(e) => updateForm("periodTo", e.target.value)} className={inputClass} />
                </FormField>
                <FormField label={labels.fields.providerReference}>
                  <input value={form.providerReference} onChange={(e) => updateForm("providerReference", e.target.value)} className={inputClass} />
                </FormField>
              </div>

              <FormField label="Convention" required>
                <select
                  value={form.conventionId}
                  onChange={(e) => updateForm("conventionId", e.target.value)}
                  className={selectClass}
                  disabled={!form.companyId || loadingConventions}
                >
                  <option value="">{loadingConventions ? "Chargement…" : "Sélectionner"}</option>
                  {conventions.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      Convention #{c.id} {c.type_traitement ? `(${c.type_traitement})` : ""}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Nature de facture" required>
                <select
                  value={form.nature}
                  onChange={(e) => updateForm("nature", e.target.value as "forfait_mensuel" | "hors_forfait")}
                  className={selectClass}
                >
                  <option value="hors_forfait">Hors forfait</option>
                  <option value="forfait_mensuel">Forfait mensuel</option>
                </select>
              </FormField>

              <FormField label={labels.fields.file} required={!hasExistingFile}>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border px-4 py-3 hover:bg-card-muted">
                  <Paperclip size={16} className="text-muted" />
                  <span className="text-sm text-foreground">
                    {fileLabel}
                    {hasExistingFile && !form.file ? ` (actuel : ${existingFileName})` : ""}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      updateForm("file", file);
                      setFileLabel(file?.name ?? labels.fields.noFileSelected);
                    }}
                  />
                </label>
                <p className="mt-1.5 text-xs text-muted">{labels.fields.attachmentHint}</p>
              </FormField>
            </>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <InvoiceCurrencyFields
                currency={form.invoiceCurrency}
                exchangeRateCdf={form.exchangeRateCdf}
                onCurrencyChange={(value) => {
                  updateForm("invoiceCurrency", value);
                  if (value === "USD") updateForm("exchangeRateCdf", "");
                }}
                onExchangeRateChange={(value) => updateForm("exchangeRateCdf", value)}
                labels={currencyFieldLabels}
              />
              {isForfait ? (
                <ForfaitQuantityFields
                  preview={selectedConvention?.forfait_preview}
                  nbPrestataire={form.forfaitNbFamilles}
                  nbClient={form.forfaitNbFamillesClient}
                  onNbPrestataireChange={(value) => updateForm("forfaitNbFamilles", value)}
                  onNbClientChange={(value) => updateForm("forfaitNbFamillesClient", value)}
                />
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField label={labels.fields.totalAmount} required hint={labels.fields.totalAmountHint}>
                    <CurrencyAmountInput
                      value={form.totalAmount}
                      onChange={(value) => updateForm("totalAmount", value)}
                      currency={form.invoiceCurrency}
                    />
                  </FormField>
                  <FormField label={labels.fields.rsspAmount} hint={labels.fields.rsspAmountHint}>
                    <CurrencyAmountInput
                      value={form.rsspAmount}
                      onChange={(value) => updateForm("rsspAmount", value)}
                      currency={form.invoiceCurrency}
                    />
                  </FormField>
                </div>
              )}
              <FormField label={labels.fields.notes}>
                <textarea value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} className={textareaClass} rows={3} />
              </FormField>
            </div>
          )}

          {step === 2 && (
            <div className="rounded-xl border border-border bg-card-muted/30 p-4 text-sm">
              <p><strong>Hôpital :</strong> {hospitalName}</p>
              <p><strong>Entreprise :</strong> {selectedCompany?.name ?? "—"}</p>
              <p><strong>Convention :</strong> {conventionLabel}</p>
              <p><strong>Période :</strong> {formatDisplayDate(form.periodFrom)} → {formatDisplayDate(form.periodTo)}</p>
              <p><strong>{labels.summary.currency} :</strong> {form.invoiceCurrency}</p>
              {form.invoiceCurrency === "CDF" && (
                <p><strong>{labels.summary.exchangeRate} :</strong> {form.exchangeRateCdf || "—"} FC/USD</p>
              )}
              <p><strong>Montant :</strong> {formatInvoiceAmount(form.totalAmount, form.invoiceCurrency)}</p>
              <p><strong>À payer :</strong> {formatInvoiceAmount(amountToPay, form.invoiceCurrency)}</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-4">
          {step === 0 && !canProceedStep0 && step0Issues.length > 0 && (
            <p className="w-full text-sm text-pink">
              {step0Issues.join(" ")}
            </p>
          )}
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1) as Step)}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-40"
          >
            <ChevronLeft size={16} /> {labels.previous}
          </button>
          {step < 2 ? (
            <button
              type="button"
              disabled={(step === 0 && !canProceedStep0) || (step === 1 && !canProceedStep1)}
              onClick={() => setStep((s) => Math.min(2, s + 1) as Step)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {labels.next} <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={() => void submit()}
              className="inline-flex items-center gap-2 rounded-lg bg-green px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              <Check size={16} /> {submitting ? "…" : isEditMode ? "Enregistrer" : labels.submit}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
